import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Button,
    Paper,
    Slide,
    Fade,
} from '@mui/material';
import { Close as CloseIcon, ArrowBackIosNew as ArrowBackIosNewIcon, ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchActivePromotions } from '../../services/promotionApi';
import { fetchPublicFeatureFlags } from '../../services/featureFlagsApi';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import './PromotionBanner.css';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PromotionBanner = () => {
    const { t, i18n } = useTranslation();
    const [promotions, setPromotions] = useState([]);
    const [popupPromotions, setPopupPromotions] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [showHideForeverDialog, setShowHideForeverDialog] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentPopupSlide, setCurrentPopupSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [promotionsEnabled, setPromotionsEnabled] = useState(false);
    const popupIntervalRef = React.useRef(null);

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Check if promotions should be shown
    const shouldShowPromotions = () => {
        const hideForever = localStorage.getItem('hidePromotionsForever');
        if (hideForever === 'true') return false;

        const lastClosedDate = localStorage.getItem('promotionLastClosedDate');
        const todayDate = getTodayDate();

        // Show if never closed or if last closed on a different date
        return !lastClosedDate || lastClosedDate !== todayDate;
    };

    useEffect(() => {
        const loadFeatureFlags = async () => {
            try {
                const flags = await fetchPublicFeatureFlags();
                const promotionFlag = flags.find(f => f.key === 'show_promotions');
                setPromotionsEnabled(promotionFlag?.value === true || promotionFlag?.value === 'true');
            } catch (error) {
                console.error('Failed to load feature flags:', error);
                setPromotionsEnabled(false);
            }
        };

        loadFeatureFlags();
    }, []);

    useEffect(() => {
        const loadPromotions = async () => {
            try {
                // Check feature flag first
                if (!promotionsEnabled) {
                    return;
                }

                // Check if promotions should be shown based on user preference
                if (!shouldShowPromotions()) {
                    return;
                }

                const data = await fetchActivePromotions();
                if (!Array.isArray(data)) return;

                setPromotions(data);

                // Get all popup promotions
                const popups = data.filter((p) => p.type === 'popup');
                if (popups.length > 0) {
                    setPopupPromotions(popups);
                    setTimeout(() => {
                        setShowPopup(true);
                    }, 1000);
                }
            } catch (error) {
                console.error('Failed to load promotions:', error);
            }
        };

        loadPromotions();
    }, [promotionsEnabled]);

    // Auto-play slider for banners
    useEffect(() => {
        const bannerPromotions = promotions.filter((p) => p.type === 'banner');
        if (bannerPromotions.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % bannerPromotions.length);
        }, 10000); // Change every 10 seconds

        return () => clearInterval(interval);
    }, [promotions]);

    // Auto-play slider for popup promotions
    useEffect(() => {
        if (!showPopup || popupPromotions.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentPopupSlide((prev) => (prev + 1) % popupPromotions.length);
        }, 8000); // Change every 8 seconds

        popupIntervalRef.current = interval;

        return () => clearInterval(interval);
    }, [showPopup, popupPromotions]);

    const handleClosePopup = () => {
        // Get current close count
        const closeCount = parseInt(localStorage.getItem('promotionCloseCount') || '0', 10);
        const newCloseCount = closeCount + 1;

        // Store today's date and increment close count
        localStorage.setItem('promotionLastClosedDate', getTodayDate());
        localStorage.setItem('promotionCloseCount', newCloseCount.toString());

        // If closed 5 or more times, ask if user wants to hide forever
        if (newCloseCount >= 5) {
            setShowHideForeverDialog(true);
        } else {
            setShowPopup(false);
            setCurrentPopupSlide(0);
        }
    };

    const handleHideForever = (hideForever) => {
        if (hideForever) {
            localStorage.setItem('hidePromotionsForever', 'true');
        }
        setShowHideForeverDialog(false);
        setShowPopup(false);
        setCurrentPopupSlide(0);
    };

    const handlePrevPopupSlide = () => {
        if (isTransitioning) return; // Prevent multiple clicks

        // Reset auto-play timer
        if (popupIntervalRef.current) {
            clearInterval(popupIntervalRef.current);
        }
        if (popupPromotions.length > 1) {
            const newInterval = setInterval(() => {
                setCurrentPopupSlide((prev) => (prev + 1) % popupPromotions.length);
            }, 8000);
            popupIntervalRef.current = newInterval;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentPopupSlide((prev) => (prev - 1 + popupPromotions.length) % popupPromotions.length);
            setTimeout(() => setIsTransitioning(false), 50);
        }, 300);
    };

    const handleNextPopupSlide = () => {
        if (isTransitioning) return; // Prevent multiple clicks

        // Reset auto-play timer
        if (popupIntervalRef.current) {
            clearInterval(popupIntervalRef.current);
        }
        if (popupPromotions.length > 1) {
            const newInterval = setInterval(() => {
                setCurrentPopupSlide((prev) => (prev + 1) % popupPromotions.length);
            }, 8000);
            popupIntervalRef.current = newInterval;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentPopupSlide((prev) => (prev + 1) % popupPromotions.length);
            setTimeout(() => setIsTransitioning(false), 50);
        }, 300);
    };

    const handlePrevSlide = () => {
        const bannerPromotions = promotions.filter((p) => p.type === 'banner');
        setCurrentSlide((prev) => (prev - 1 + bannerPromotions.length) % bannerPromotions.length);
    };

    const handleNextSlide = () => {
        const bannerPromotions = promotions.filter((p) => p.type === 'banner');
        setCurrentSlide((prev) => (prev + 1) % bannerPromotions.length);
    };

    const bannerPromotions = promotions.filter((p) => p.type === 'banner');
    const currentLang = i18n.language;

    return (
        <>
            {/* Banner Promotions Slider */}
            {bannerPromotions.length > 0 && (
                <Box className="promotion-banner-container">
                    <Box className="promotion-slider">
                        {/* Current Slide */}
                        <Box className="promotion-slide-wrapper">
                            <Paper className="promotion-banner active" elevation={3}>
                                <Box className="promotion-banner-content">
                                    {bannerPromotions[currentSlide].imageUrl && (
                                        <img
                                            src={bannerPromotions[currentSlide].imageUrl}
                                            alt={bannerPromotions[currentSlide].title}
                                            className="promotion-banner-image"
                                        />
                                    )}
                                    {(bannerPromotions[currentSlide].title || bannerPromotions[currentSlide].description) && (
                                        <Box className="promotion-banner-text">
                                            {bannerPromotions[currentSlide].title && (
                                                <Typography variant="h5" className="promotion-banner-title">
                                                    {bannerPromotions[currentSlide].title}
                                                </Typography>
                                            )}
                                            {bannerPromotions[currentSlide].description && (
                                                <Typography
                                                    variant="body1"
                                                    className="promotion-banner-description"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(bannerPromotions[currentSlide].description) }}
                                                />
                                            )}
                                            {bannerPromotions[currentSlide].discountPercentage && (
                                                <Box className="promotion-discount-badge">
                                                    -{bannerPromotions[currentSlide].discountPercentage}%
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Paper>

                            {/* Preview of Next Slide */}
                            {bannerPromotions.length > 1 && (
                                <Paper
                                    className="promotion-banner preview"
                                    elevation={1}
                                    onClick={handleNextSlide}
                                >
                                    <Box className="promotion-banner-content">
                                        {bannerPromotions[(currentSlide + 1) % bannerPromotions.length].imageUrl && (
                                            <img
                                                src={bannerPromotions[(currentSlide + 1) % bannerPromotions.length].imageUrl}
                                                alt={bannerPromotions[(currentSlide + 1) % bannerPromotions.length].title}
                                                className="promotion-banner-image"
                                            />
                                        )}
                                        {bannerPromotions[(currentSlide + 1) % bannerPromotions.length].title && (
                                            <Box className="promotion-banner-text">
                                                <Typography variant="h6" className="promotion-banner-title">
                                                    {bannerPromotions[(currentSlide + 1) % bannerPromotions.length].title}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            )}
                        </Box>

                        {/* Navigation Arrows */}
                        {bannerPromotions.length > 1 && (
                            <>
                                <IconButton
                                    className="slider-arrow slider-arrow-left"
                                    onClick={handlePrevSlide}
                                    sx={{
                                        position: 'absolute',
                                        left: 10,
                                        top: '40%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { backgroundColor: 'white' },
                                        zIndex: 10,
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </IconButton>
                                <IconButton
                                    className="slider-arrow slider-arrow-right"
                                    onClick={handleNextSlide}
                                    sx={{
                                        position: 'absolute',
                                        right: 10,
                                        top: '40%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { backgroundColor: 'white' },
                                        zIndex: 10,
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>

                                {/* Slide Indicators */}
                                <Box className="slider-indicators">
                                    {bannerPromotions.map((_, index) => (
                                        <Box
                                            key={index}
                                            className={`slider-indicator ${index === currentSlide ? 'active' : ''}`}
                                            onClick={() => setCurrentSlide(index)}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {/* Popup Promotion */}
            {popupPromotions.length > 0 && (
                <Dialog
                    open={showPopup}
                    onClose={handleClosePopup}
                    TransitionComponent={Transition}
                    maxWidth="sm"
                    fullWidth
                    className="promotion-popup"
                >
                    <IconButton
                        onClick={handleClosePopup}
                        className="promotion-popup-close"
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent sx={{ p: 0, position: 'relative' }}>
                        {/* Navigation Arrows for Multiple Popups */}
                        {popupPromotions.length > 1 && (
                            <>
                                <IconButton
                                    onClick={handlePrevPopupSlide}
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            transform: 'translateY(-50%) scale(1.1)',
                                        },
                                        zIndex: 10,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </IconButton>
                                <IconButton
                                    onClick={handleNextPopupSlide}
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            transform: 'translateY(-50%) scale(1.1)',
                                        },
                                        zIndex: 10,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>
                                {/* Slide Counter */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: '20px',
                                        zIndex: 10,
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                    }}
                                >
                                    {currentPopupSlide + 1} / {popupPromotions.length}
                                </Box>
                            </>
                        )}

                        <Fade in={!isTransitioning} timeout={300} key={currentPopupSlide}>
                            <Box className="promotion-popup-content">
                                {popupPromotions[currentPopupSlide].imageUrl && (
                                    <img
                                        src={popupPromotions[currentPopupSlide].imageUrl}
                                        alt={popupPromotions[currentPopupSlide].title}
                                        className="promotion-popup-image"
                                    />
                                )}
                                {(popupPromotions[currentPopupSlide].title || popupPromotions[currentPopupSlide].description || popupPromotions[currentPopupSlide].discountPercentage) && (
                                    <Box className="promotion-popup-text">
                                        {popupPromotions[currentPopupSlide].discountPercentage && (
                                            <Box className="promotion-popup-discount">
                                                -{popupPromotions[currentPopupSlide].discountPercentage}%
                                            </Box>
                                        )}
                                        {popupPromotions[currentPopupSlide].title && (
                                            <Typography variant="h5" className="promotion-popup-title">
                                                {popupPromotions[currentPopupSlide].title}
                                            </Typography>
                                        )}
                                        {popupPromotions[currentPopupSlide].description && (
                                            <Typography
                                                variant="body1"
                                                className="promotion-popup-description"
                                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(popupPromotions[currentPopupSlide].description) }}
                                                sx={{ mt: 2 }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Fade>
                    </DialogContent>
                </Dialog>
            )}

            {/* Hide Forever Confirmation Dialog */}
            <Dialog
                open={showHideForeverDialog}
                onClose={() => handleHideForever(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {currentLang === 'bg' ? 'Скриване на промоции' : 'Hide Promotions'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        {currentLang === 'bg'
                            ? 'Забелязахме, че затваряте промоциите често. Искате ли да ги скриете завинаги?'
                            : 'We noticed you close promotions frequently. Would you like to hide them forever?'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        onClick={() => handleHideForever(false)}
                        variant="outlined"
                        color="primary"
                    >
                        {currentLang === 'bg' ? 'Не' : 'No'}
                    </Button>
                    <Button
                        onClick={() => handleHideForever(true)}
                        variant="contained"
                        color="primary"
                    >
                        {currentLang === 'bg' ? 'Да, скрий ги' : 'Yes, hide them'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PromotionBanner;
